import { css } from '@emotion/react';
import { PayoutStatus } from '@frontend/api-payouts';
import { Divider } from '@frontend/divider';
import { useTranslation } from '@frontend/i18n';
import { PaymentsFeatureFlags } from '@frontend/payments-hooks';
import { useAppScopeStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { formatCentsToCurrency } from '../../../utils/currency-helpers';
import { DetailCard } from '../../Card/detail-card';
import { SummaryCardItem } from '../../InvoiceDetails/SummaryCardItem';
import { PayoutStatusChip } from '../../Payouts/PayoutStatusChip/index';

type PayoutProps = {
  amount: number;
  currency: string;
  status: PayoutStatus | '';
  estimatedArrivalOn: string | number;
  payoutId: string;
  descriptor: string;
};

export const PayoutSummaryCard = ({ payout }: { payout: PayoutProps }) => {
  const { t } = useTranslation('payments');
  const { getFeatureFlagValue } = useAppFlagStore();
  const { selectedLocationIds } = useAppScopeStore();
  const showPayoutDescriptor = getFeatureFlagValue({
    flagName: PaymentsFeatureFlags.PayoutDescriptorColumn,
    locationIds: selectedLocationIds,
  });

  return (
    <DetailCard>
      <div
        css={css`
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: flex-start;
          width: 100%;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            margin-right: ${theme.spacing(3)};
          `}
        >
          <Text as='h2' color='light' size='large' css={{ margin: 0 }}>
            {t('Payout')}
          </Text>
          <Text
            weight='bold'
            css={css`
              display: flex;
              align-items: baseline;
              margin: 0;
              font-size: ${theme.fontSize(24)};
            `}
          >
            {formatCentsToCurrency(payout.amount)}
            <Text
              as='span'
              color='light'
              weight='regular'
              size='inherit'
              css={css`
                margin: ${theme.spacing(0, 0, 0, 1)};
              `}
            >
              {payout.currency}
            </Text>
            {payout.status !== '' && (
              <PayoutStatusChip
                status={payout.status}
                as='span'
                css={css`
                  margin-left: ${theme.spacing(2)};
                  align-self: center;
                `}
              />
            )}
          </Text>
        </div>
      </div>
      <Divider
        css={css`
          margin: ${theme.spacing(2, 0, 0)};
        `}
      />
      <div
        css={css`
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          justify-content: flex-start;
          > p:not(:last-child) {
            margin: ${theme.spacing(2, 5, 0, 0)};
          }
          > p:last-child {
            margin: ${theme.spacing(2, 0, 0)};
          }
        `}
      >
        {/* we don't need to format date here, because it is being formatted before it is passed in */}
        <SummaryCardItem label={t('Date Paid')}>{payout.estimatedArrivalOn}</SummaryCardItem>
        <SummaryCardItem label={t('Amount')}>{formatCentsToCurrency(payout.amount)}</SummaryCardItem>
        <SummaryCardItem label={t('ID')}>{payout.payoutId}</SummaryCardItem>
        {showPayoutDescriptor && <SummaryCardItem label={t('Payout Descriptor')}>{payout?.descriptor}</SummaryCardItem>}
      </div>
    </DetailCard>
  );
};
